// Enhanced Costing Service - Integrates pure fuel cost calculation with existing costing system
// This service provides costing data with real-time fuel cost calculation

import { shipmentCostingsService } from './shipment-costings';
import { fuelCostCalculator } from './fuel-cost-calculator';

class EnhancedCostingService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 2 * 60 * 1000; // 2 minutes
  }

  // Get enhanced costing data with real-time fuel calculation
  async getEnhancedCosting(companyId, shipmentId) {
    try {
      const cacheKey = `${companyId}_${shipmentId}`;
      const cached = this.cache.get(cacheKey);
      
      if (cached && (Date.now() - cached.timestamp) < this.cacheTimeout) {
        return cached.data;
      }

      // Get stored costing data (for manual costs, maintenance, etc.)
      const storedCosting = await shipmentCostingsService.getCostingByShipment(shipmentId);
      
      // Calculate real-time fuel costs (no duplication)
      const fuelCosts = await fuelCostCalculator.calculateFuelCostsForShipment(companyId, shipmentId);

      // Merge stored costing with real-time fuel calculation
      const enhancedCosting = {
        // Use stored costing as base (for non-fuel costs)
        ...(storedCosting || {}),
        
        // Override fuel costs with real-time calculation
        petroAppFuelAmount: fuelCosts.petroAppFuelAmount,
        petroAppFuelBaseAmount: fuelCosts.petroAppFuelBaseAmount,
        petroAppFuelVATAmount: fuelCosts.petroAppFuelVATAmount,
        
        manualFuelAmount: fuelCosts.manualFuelAmount,
        manualFuelBaseAmount: fuelCosts.manualFuelBaseAmount,
        manualFuelVATAmount: fuelCosts.manualFuelVATAmount,
        
        totalFuelAmount: fuelCosts.totalFuelAmount,
        totalFuelBaseAmount: fuelCosts.totalFuelBaseAmount,
        totalFuelVATAmount: fuelCosts.totalFuelVATAmount,
        
        // Note: Legacy fuel fields are no longer set - use totalFuelAmount instead
        
        // Additional fuel metadata
        fuelTransactionCount: fuelCosts.petroAppTransactionCount,
        fuelTotalLiters: fuelCosts.petroAppTotalLiters,
        fuelAverageCostPerLiter: fuelCosts.averageCostPerLiter,
        
        // Flag to indicate enhanced calculation
        isEnhancedCosting: true,
        fuelCalculatedAt: fuelCosts.calculatedAt,
        
        // Ensure we have default values for other costs
        maintenanceAmount: storedCosting?.maintenanceAmount || 0,
        commissionAmount: storedCosting?.commissionAmount || 0,
        stops: storedCosting?.stops || [],
        otherCosts: storedCosting?.otherCosts || []
      };

      // Cache the result
      this.cache.set(cacheKey, {
        data: enhancedCosting,
        timestamp: Date.now()
      });

      return enhancedCosting;

    } catch (error) {
      console.error(`Error getting enhanced costing for shipment ${shipmentId}:`, error);
      
      // Fallback to stored costing if calculation fails
      try {
        const fallbackCosting = await shipmentCostingsService.getCostingByShipment(shipmentId);
        return fallbackCosting || {};
      } catch (fallbackError) {
        console.error('Fallback costing also failed:', fallbackError);
        return {};
      }
    }
  }

  // Get enhanced costings for multiple shipments
  async getEnhancedCostings(companyId, shipmentIds) {
    try {
      const results = new Map();
      
      // Process in parallel for better performance
      const promises = shipmentIds.map(async (shipmentId) => {
        try {
          const enhancedCosting = await this.getEnhancedCosting(companyId, shipmentId);
          results.set(shipmentId, enhancedCosting);
        } catch (error) {
          console.error(`Error getting enhanced costing for shipment ${shipmentId}:`, error);
          results.set(shipmentId, {});
        }
      });

      await Promise.all(promises);
      return results;

    } catch (error) {
      console.error('Error getting enhanced costings for multiple shipments:', error);
      throw error;
    }
  }

  // Get enhanced costings for all shipments in a company
  async getAllEnhancedCostings(companyId) {
    try {
      // Get all stored costings first
      const storedCostings = await shipmentCostingsService.getCostingsByCompany(companyId);
      
      // Get all shipment IDs that have costings
      const shipmentIds = storedCostings.map(costing => costing.shipmentId);
      
      // Get enhanced costings for all shipments
      const enhancedCostingsMap = await this.getEnhancedCostings(companyId, shipmentIds);
      
      // Convert map to array format
      const enhancedCostings = Array.from(enhancedCostingsMap.entries()).map(([shipmentId, costing]) => ({
        ...costing,
        shipmentId
      }));

      return enhancedCostings;

    } catch (error) {
      console.error('Error getting all enhanced costings:', error);
      throw error;
    }
  }

  // Clear cache for a specific shipment
  clearShipmentCache(companyId, shipmentId) {
    const cacheKey = `${companyId}_${shipmentId}`;
    this.cache.delete(cacheKey);
    
    // Also clear fuel calculator cache
    fuelCostCalculator.clearShipmentCache(companyId, shipmentId);
  }

  // Clear all cache
  clearAllCache() {
    this.cache.clear();
    fuelCostCalculator.clearAllCache();
  }

  // Refresh enhanced costing for a shipment
  async refreshEnhancedCosting(companyId, shipmentId) {
    this.clearShipmentCache(companyId, shipmentId);
    return await this.getEnhancedCosting(companyId, shipmentId);
  }

  // Update manual fuel amount (the only fuel-related thing that should be stored)
  async updateManualFuelAmount(companyId, shipmentId, manualFuelAmount, manualFuelIncludesVAT, userId) {
    try {
      // Get or create costing
      let costing = await shipmentCostingsService.getCostingByShipment(shipmentId);
      
      if (!costing) {
        // Create new costing with only manual fuel data
        const costingData = {
          companyId,
          shipmentId,
          manualFuelAmount: parseFloat(manualFuelAmount) || 0,
          manualFuelIncludesVAT: manualFuelIncludesVAT !== false,
          createdBy: userId
        };
        
        costing = await shipmentCostingsService.addCosting(costingData);
      } else {
        // Update existing costing with manual fuel data only
        await shipmentCostingsService.updateCosting(costing.id, {
          manualFuelAmount: parseFloat(manualFuelAmount) || 0,
          manualFuelIncludesVAT: manualFuelIncludesVAT !== false,
          updatedBy: userId
        });
      }

      // Clear cache to force recalculation
      this.clearShipmentCache(companyId, shipmentId);

      // Return updated enhanced costing
      return await this.getEnhancedCosting(companyId, shipmentId);

    } catch (error) {
      console.error('Error updating manual fuel amount:', error);
      throw error;
    }
  }
}

export const enhancedCostingService = new EnhancedCostingService();